import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import HomePage from './pages/HomePage';
import './index.css';
import SessionTerminalPage from './pages/SessionTerminalPage';
import CardPage from './pages/CardPage';
import CoursePageStart from './pages/CoursePageStart';
import { loadCoursesData } from './services/courseData';
import { AuthProvider } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import PrivateRoute from './components/PrivateRoute';
import AdminPage from './pages/AdminPage';
import PresentationPage from './pages/PresentationPage';
import DemoLessonPage from './pages/DemoLessonPage';
import TestPage from './pages/TestPage';
import { Alert, Box, CircularProgress } from '@mui/material';

const root = ReactDOM.createRoot(document.getElementById('root'));

function CoursesRoute() {
  const [courses, setCourses] = React.useState([]);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    loadCoursesData()
      .then((data) => {
        if (active) {
          setCourses(data.courses || []);
        }
      })
      .catch((error) => {
        if (active) {
          setLoadError(error);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (loadError) {
    return <div>Unable to load course data.</div>;
  }

  return <PrivateRoute><CoursePageStart courses={courses} /></PrivateRoute>;
}

function DemoRoute() {
  const [state, setState] = React.useState({ loading: true, enabled: false, error: false });

  React.useEffect(() => {
    let active = true;
    fetch('/demo-status')
      .then((response) => {
        if (!response.ok) throw new Error('Unable to check demo availability.');
        return response.json();
      })
      .then((data) => active && setState({ loading: false, enabled: Boolean(data.enabled), error: false }))
      .catch(() => active && setState({ loading: false, enabled: false, error: true }));
    return () => { active = false; };
  }, []);

  if (state.loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress aria-label="Checking demo availability" /></Box>;
  }
  if (!state.enabled) {
    return <Alert severity={state.error ? 'warning' : 'info'}>{state.error ? 'Demo availability could not be checked. Please try again later.' : 'The demo is currently unavailable.'}</Alert>;
  }
  return <DemoLessonPage />;
}

root.render(
  <React.StrictMode>
    <AuthProvider>
      <Router>
        <Routes>
          <Route path='/' element={<App />}>
            <Route index element={<HomePage />} />
            <Route path='login' element={<LoginPage />} />
            <Route path='signup' element={<Navigate to='/login' replace />} />
            <Route
              path='profile'
              element={
                <PrivateRoute>
                  <ProfilePage />
                </PrivateRoute>
              }
            />
            <Route
              path='admin'
              element={
                <PrivateRoute>
                  <AdminPage />
                </PrivateRoute>
              }
            />
            <Route
              path='presentation'
              element={
                <PrivateRoute>
                  <PresentationPage />
                </PrivateRoute>
              }
            />
            <Route path='test' element={<PrivateRoute><TestPage /></PrivateRoute>} />
            <Route path='session/:slug' element={<SessionTerminalPage />} />
            <Route path='lesson' element={<CardPage />} />
            <Route path='demo' element={<DemoRoute />} />
            <Route path='courses/:courseId' element={<CoursesRoute />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  </React.StrictMode>
);
